/**
 * AdminRoadmapForm.tsx
 * Multi-step form for creating/editing premium roadmaps
 * 
 * Steps:
 * 1. Roadmap Info (title, description, type, difficulty, total days)
 * 2. Weeks Input (week title and subtitle)
 * 3. Days Input (day title and topics)
 * 4. Video & Resources (video IDs and resource links)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import type { AdminRoadmapDay, AdminRoadmapData } from '@/services/adminRoadmapService';

interface AdminRoadmapFormProps {
  initialData?: AdminRoadmapData;
  onSubmit: (data: AdminRoadmapData) => Promise<void>;
  onCancel: () => void;
  isDark: boolean;
  isLoading?: boolean;
}

export function AdminRoadmapForm({
  initialData,
  onSubmit,
  onCancel,
  isDark,
  isLoading = false,
}: AdminRoadmapFormProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Roadmap Info
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [totalDays, setTotalDays] = useState(
    initialData?.total_days?.toString() || '28'
  );
  const [roadmapType, setRoadmapType] = useState<
    'Skill' | 'Academic' | 'Religious'
  >(initialData?.roadmap_type || 'Skill');
  const [difficulty, setDifficulty] = useState(
    initialData?.difficulty || 'Beginner'
  );
  const [isPublished, setIsPublished] = useState(initialData?.is_published || false);

  // Step 2 & 3: Days data
  const [days, setDays] = useState<AdminRoadmapDay[]>(
    initialData?.days || Array.from({ length: parseInt(totalDays) || 28 }, (_, i) => ({
      day_number: i + 1,
      title: `Day ${i + 1}`,
      subtitle: '',
      description: '',
      video_id: '',
      video_url: '',
      resources: { links: [] },
    }))
  );

  // Step 2: Week goals and subtitles
  const [weekData, setWeekData] = useState<Record<number, { title: string; subtitle: string }>>(
    initialData?.days?.reduce((acc, day) => {
      const weekNum = Math.ceil(day.day_number / 7);
      if (!acc[weekNum]) {
        acc[weekNum] = { title: `Week ${weekNum}`, subtitle: '' };
      }
      return acc;
    }, {} as Record<number, { title: string; subtitle: string }>) || 
    Object.fromEntries(
      Array.from({ length: Math.ceil((parseInt(totalDays) || 28) / 7) }, (_, i) => [
        i + 1,
        { title: `Week ${i + 1}`, subtitle: '' }
      ])
    )
  );

  // Calculate weeks
  const totalWeeks = useMemo(() => {
    return Math.ceil((parseInt(totalDays) || 28) / 7);
  }, [totalDays]);

  // Validate Step 1
  const validateStep1 = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = 'Title is required';
    if (title.length > 255) newErrors.title = 'Title too long (max 255 chars)';

    const numDays = parseInt(totalDays);
    if (isNaN(numDays) || numDays <= 0) newErrors.totalDays = 'Total days must be positive';
    if (numDays > 365) newErrors.totalDays = 'Total days cannot exceed 365';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title, totalDays]);

  // Validate Step 2: Weeks
  const validateStep2 = useCallback(() => {
    const newErrors: Record<string, string> = {};

    Object.entries(weekData).forEach(([weekNum, data]) => {
      if (!data.title?.trim()) {
        newErrors[`week_${weekNum}_title`] = `Week ${weekNum} title is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [weekData]);

  // Validate Step 3: Days
  const validateStep3 = useCallback(() => {
    const newErrors: Record<string, string> = {};

    days.forEach((day, idx) => {
      if (!day.title?.trim()) {
        newErrors[`day_${idx}_title`] = `Day ${idx + 1} title is required`;
      }
      if (!day.description?.trim()) {
        newErrors[`day_${idx}_description`] = `Day ${idx + 1} topics is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [days]);

  // Validate Step 4
  const validateStep4 = useCallback(() => {
    // Step 4 is optional - videos and resources can be added later
    return true;
  }, []);

  // Handle day field change
  const updateDay = useCallback(
    (dayIndex: number, field: keyof AdminRoadmapDay, value: any) => {
      setDays((prev) => {
        const updated = [...prev];
        updated[dayIndex] = { ...updated[dayIndex], [field]: value };
        return updated;
      });
    },
    []
  );

  // Update resource link
  const updateResourceLink = useCallback(
    (dayIndex: number, linkIndex: number, field: string, value: string) => {
      setDays((prev) => {
        const updated = [...prev];
        const links = updated[dayIndex].resources?.links || [];
        if (links[linkIndex]) {
          links[linkIndex] = { ...links[linkIndex], [field]: value };
          updated[dayIndex].resources = { ...updated[dayIndex].resources, links };
        }
        return updated;
      });
    },
    []
  );

  // Add resource link
  const addResourceLink = useCallback((dayIndex: number) => {
    setDays((prev) => {
      const updated = [...prev];
      const links = updated[dayIndex].resources?.links || [];
      links.push({ title: '', url: '', type: 'article' });
      updated[dayIndex].resources = { ...updated[dayIndex].resources, links };
      return updated;
    });
  }, []);

  // Remove resource link
  const removeResourceLink = useCallback((dayIndex: number, linkIndex: number) => {
    setDays((prev) => {
      const updated = [...prev];
      const links = updated[dayIndex].resources?.links || [];
      links.splice(linkIndex, 1);
      updated[dayIndex].resources = { ...updated[dayIndex].resources, links };
      return updated;
    });
  }, []);

  // Handle next step
  const handleNext = () => {
    if (step === 1) {
      if (validateStep1()) {
        // Adjust days array if total_days changed
        const newTotalDays = parseInt(totalDays);
        if (newTotalDays !== days.length) {
          const adjustedDays = Array.from(
            { length: newTotalDays },
            (_, i) =>
              days[i] || {
                day_number: i + 1,
                title: `Day ${i + 1}`,
                subtitle: '',
                description: '',
                video_id: '',
                video_url: '',
                resources: { links: [] },
              }
          );
          setDays(adjustedDays);
        }
        // Initialize week data
        const weeks: Record<number, { title: string; subtitle: string }> = {};
        for (let i = 1; i <= totalWeeks; i++) {
          weeks[i] = { title: `Week ${i}`, subtitle: '' };
        }
        setWeekData(weeks);
        setStep(2);
      }
    } else if (step === 2) {
      if (validateStep2()) {
        setStep(3);
      }
    } else if (step === 3) {
      if (validateStep3()) {
        setStep(4);
      }
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (step !== 4) return;
    if (!validateStep4()) return;

    setSubmitting(true);
    try {
      const formData: AdminRoadmapData = {
        title,
        description,
        total_days: parseInt(totalDays),
        roadmap_type: roadmapType,
        difficulty: difficulty as 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert',
        days,
        is_published: isPublished,
      };

      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const bgColor = isDark ? 'bg-zinc-900' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-zinc-900';
  const borderColor = isDark ? 'border-zinc-700' : 'border-zinc-200';
  const inputBg = isDark ? 'bg-zinc-800' : 'bg-zinc-50';
  const inputBorder = isDark ? 'border-zinc-700' : 'border-zinc-300';

  return (
    <div className={`${bgColor} rounded-xl border ${borderColor} p-6 space-y-6 max-h-[80vh] overflow-y-auto`}>
      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-2xl font-bold ${textColor}`}>
          Create Premium Roadmap
        </h2>
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s
                  ? 'bg-red-500 text-white'
                  : isDark
                    ? 'bg-zinc-700 text-zinc-400'
                    : 'bg-zinc-200 text-zinc-600'
              }`}
            >
              {s}
            </div>
          ))}
        </div>
      </div>

      {/* STEP 1: Basic Info */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              Roadmap Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Learn Python from Zero to Hero"
              className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500`}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.title}
              </p>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what learners will achieve"
              rows={3}
              className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500`}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Total Days *
              </label>
              <input
                type="number"
                value={totalDays}
                onChange={(e) => setTotalDays(e.target.value)}
                min="1"
                max="365"
                className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500`}
              />
              {errors.totalDays && (
                <p className="text-red-500 text-sm mt-1">{errors.totalDays}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Type *
              </label>
              <select
                value={roadmapType}
                onChange={(e) =>
                  setRoadmapType(e.target.value as 'Skill' | 'Academic' | 'Religious')
                }
                className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500`}
              >
                <option value="Skill">Skill</option>
                <option value="Academic">Academic</option>
                <option value="Religious">Religious</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Difficulty
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert')}
                className={`w-full px-4 py-2 rounded-lg border ${inputBg} ${inputBorder} ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500`}
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-zinc-700">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="w-5 h-5 rounded border-zinc-600 text-red-600 focus:ring-red-500"
              />
              <span className={`font-medium ${textColor}`}>
                📢 Publish to Dashboard
              </span>
            </label>
            <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-600'} mt-2 ml-8`}>
              {isPublished 
                ? '✅ Users will see this roadmap on their dashboard' 
                : '🔒 This roadmap is in draft mode (not visible to users)'}
            </p>
          </div>
        </div>
      )}

      {/* STEP 2: Weeks Title & Subtitle */}
      {step === 2 && (
        <div className="space-y-4">
          <h3 className={`text-lg font-semibold ${textColor} mb-4`}>
            Week Titles & Subtitles
          </h3>
          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-4`}>
            Give each week a title and description for better organization.
          </p>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((weekNum) => (
              <div key={weekNum} className={`p-4 rounded-lg border ${borderColor} ${inputBg} space-y-3`}>
                <h4 className={`font-semibold ${textColor}`}>Week {weekNum}</h4>

                {/* Week Title */}
                <div>
                  <label className={`text-sm font-medium ${textColor} block mb-1`}>
                    Week Title *
                  </label>
                  <input
                    type="text"
                    value={weekData[weekNum]?.title || ''}
                    onChange={(e) =>
                      setWeekData((prev) => ({
                        ...prev,
                        [weekNum]: { ...prev[weekNum], title: e.target.value },
                      }))
                    }
                    placeholder={`e.g., Foundations`}
                    className={`w-full px-3 py-2 rounded border ${borderColor} bg-transparent ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500 text-sm`}
                  />
                  {errors[`week_${weekNum}_title`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`week_${weekNum}_title`]}</p>
                  )}
                </div>

                {/* Week Subtitle */}
                <div>
                  <label className={`text-sm font-medium ${textColor} block mb-1`}>
                    Week Subtitle
                  </label>
                  <input
                    type="text"
                    value={weekData[weekNum]?.subtitle || ''}
                    onChange={(e) =>
                      setWeekData((prev) => ({
                        ...prev,
                        [weekNum]: { ...prev[weekNum], subtitle: e.target.value },
                      }))
                    }
                    placeholder="e.g., Learn the basics"
                    className={`w-full px-3 py-2 rounded border ${borderColor} bg-transparent ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500 text-sm`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 3: Days Title & Topics */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className={`text-lg font-semibold ${textColor} mb-4`}>
            Day Title & Topics
          </h3>
          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-4`}>
            Add the day title and topics for each lesson. These will appear in the roadmap viewer.
          </p>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {days.map((day, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${borderColor} ${inputBg} space-y-3`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-red-500">Day {idx + 1}</span>
                  {day.title && day.description && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                </div>

                {/* Day Title */}
                <div>
                  <label className={`text-sm font-medium ${textColor} block mb-1`}>
                    Day Title *
                  </label>
                  <input
                    type="text"
                    value={day.title}
                    onChange={(e) => updateDay(idx, 'title', e.target.value)}
                    placeholder="e.g., Getting Started with Python"
                    className={`w-full px-3 py-2 rounded border ${borderColor} bg-transparent ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500 text-sm`}
                  />
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'} mt-1`}>
                    What is the main topic for this day?
                  </p>
                  {errors[`day_${idx}_title`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`day_${idx}_title`]}</p>
                  )}
                </div>

                {/* Task Description */}
                <div>
                  <label className={`text-sm font-medium ${textColor} block mb-1`}>
                    Task Description *
                  </label>
                  <textarea
                    value={day.description || ''}
                    onChange={(e) => updateDay(idx, 'description', e.target.value)}
                    placeholder="What should the learner do today? (e.g., Install Python, set up IDE, write first program)"
                    rows={3}
                    className={`w-full px-3 py-2 rounded border ${borderColor} bg-transparent ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500 text-sm`}
                  />
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'} mt-1`}>
                    Details about what learners will accomplish today
                  </p>
                  {errors[`day_${idx}_description`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`day_${idx}_description`]}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 4: Video IDs & Resources */}
      {step === 4 && (
        <div className="space-y-4">
          <h3 className={`text-lg font-semibold ${textColor} mb-4`}>
            Video & Resources (Optional)
          </h3>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {days.map((day, dayIdx) => (
              <div key={dayIdx} className={`p-4 rounded-lg border ${borderColor} ${inputBg} space-y-3`}>
                <h4 className={`font-semibold ${textColor}`}>
                  {day.title || `Day ${dayIdx + 1}`}
                </h4>

                {/* Video ID */}
                <div>
                  <label className={`text-sm font-medium ${textColor} block mb-1`}>
                    YouTube Video ID
                  </label>
                  <input
                    type="text"
                    value={day.video_id || ''}
                    onChange={(e) => updateDay(dayIdx, 'video_id', e.target.value)}
                    placeholder="e.g., dQw4w9WgXcQ"
                    className={`w-full px-3 py-2 rounded border ${borderColor} bg-transparent ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500 text-sm`}
                  />
                  <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'} mt-1`}>
                    Just the ID from youtube.com/watch?v=ID
                  </p>
                </div>

                {/* Resources */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`text-sm font-medium ${textColor}`}>
                      Resources
                    </label>
                    <button
                      onClick={() => addResourceLink(dayIdx)}
                      className="flex items-center gap-1 text-red-500 hover:text-red-600 text-sm"
                    >
                      <Plus className="w-4 h-4" /> Add Link
                    </button>
                  </div>

                  {day.resources?.links && day.resources.links.length > 0 ? (
                    <div className="space-y-2">
                      {day.resources.links.map((link, linkIdx) => (
                        <div key={linkIdx} className="flex gap-2">
                          <input
                            type="text"
                            value={link.title || ''}
                            onChange={(e) =>
                              updateResourceLink(dayIdx, linkIdx, 'title', e.target.value)
                            }
                            placeholder="Resource title"
                            className={`flex-1 px-3 py-2 rounded border ${borderColor} bg-transparent ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500 text-sm`}
                          />
                          <input
                            type="url"
                            value={link.url || ''}
                            onChange={(e) =>
                              updateResourceLink(dayIdx, linkIdx, 'url', e.target.value)
                            }
                            placeholder="https://..."
                            className={`flex-1 px-3 py-2 rounded border ${borderColor} bg-transparent ${textColor} focus:outline-none focus:ring-2 focus:ring-red-500 text-sm`}
                          />
                          <button
                            onClick={() => removeResourceLink(dayIdx, linkIdx)}
                            className="text-red-500 hover:text-red-600 p-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      No resources added yet
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-6 border-t" style={{ borderColor: borderColor }}>
        <button
          onClick={onCancel}
          className={`px-6 py-2 rounded-lg border ${borderColor} ${textColor} hover:opacity-80 transition`}
        >
          Cancel
        </button>

        <div className="flex gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep((prev) => (prev - 1) as 1 | 2 | 3 | 4)}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-zinc-700 text-white hover:bg-zinc-600 transition"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          )}

          {step < 4 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || isLoading}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {submitting || isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {initialData ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" /> {initialData ? 'Update Roadmap' : 'Create Roadmap'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
